import { useEffect, useState } from 'react';
import { listAppPanels } from '../../api/extensions.api';

export const PLUGIN_PANEL_CSP = "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data:; font-src data:";

export const MAX_RUN_INPUT_BYTES = 20000;

/** Scripts only, plus links that open in a new, unsandboxed tab. No same-origin access, forms, or top navigation. */
export const PLUGIN_PANEL_SANDBOX = 'allow-scripts allow-popups allow-popups-to-escape-sandbox';

/**
 * Runs inside the plugin frame. Gives plugin HTML `window.interviewflow`:
 * onData(cb) for the panel data, run(input) to run the panel flow, resize(px) to change height.
 */
const BRIDGE = `(function(){
var handlers=[],last=null;
window.addEventListener('message',function(e){
  if(e.source!==window.parent)return;
  var m=e.data;
  if(!m||m.type!=='interviewflow:data')return;
  last=m;
  handlers.forEach(function(h){try{h(m.data,m);}catch(err){console.error(err);}});
});
document.addEventListener('click',function(e){
  var a=e.target&&e.target.closest?e.target.closest('a[href]'):null;
  if(!a)return;
  var href=a.getAttribute('href')||'';
  if(/^https?:/i.test(a.href)&&href.charAt(0)!=='#'){a.target='_blank';a.rel='noopener noreferrer';}
  else if(href.charAt(0)!=='#'){e.preventDefault();}
},true);
function fakeSubmit(form,e){e.preventDefault();form.dispatchEvent(new Event('submit',{bubbles:true,cancelable:true}));}
document.addEventListener('click',function(e){
  var b=e.target&&e.target.closest?e.target.closest('button,input[type=submit],input[type=image]'):null;
  if(!b||!b.form)return;
  if(b.tagName==='BUTTON'&&(b.getAttribute('type')||'submit').toLowerCase()!=='submit')return;
  fakeSubmit(b.form,e);
},true);
document.addEventListener('keydown',function(e){
  var t=e.target;
  if(e.key!=='Enter'||!t||t.tagName!=='INPUT'||!t.form)return;
  if(/^(button|checkbox|radio|file|reset|submit|image|color|range)$/i.test(t.type))return;
  fakeSubmit(t.form,e);
},true);
function send(msg){window.parent.postMessage(msg,'*');}
window.interviewflow={
  onData:function(h){if(typeof h!=='function')return;handlers.push(h);if(last){try{h(last.data,last);}catch(err){console.error(err);}}},
  run:function(input){send({type:'interviewflow:run',input:input&&typeof input==='object'?input:{}});},
  resize:function(px){send({type:'interviewflow:resize',height:Number(px)||0});}
};
send({type:'interviewflow:ready'});
})();`;

export function pluginPanelDocument(html) {
  return `<!doctype html><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${PLUGIN_PANEL_CSP}"><script>${BRIDGE}</script>${html || ''}`;
}

export function clampPanelHeight(px, fallback = 360) {
  const n = Number(px);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.min(1600, Math.max(120, Math.round(n)));
}

/** A flow input sent by a plugin frame, or null when it is not a small plain JSON object. */
export function safeRunInput(input) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
  try {
    const json = JSON.stringify(input);
    if (json.length > MAX_RUN_INPUT_BYTES) return null;
    return JSON.parse(json);
  } catch {
    return null;
  }
}

let workspacePanels = null;

export function loadWorkspacePluginPanels({ force = false } = {}) {
  if (!workspacePanels || force) {
    workspacePanels = listAppPanels().then(({ panels, error }) => {
      if (error) workspacePanels = null;
      return { panels, error };
    });
  }
  return workspacePanels;
}

/** Plugin panels for an app: the ones embedded in a public app payload, else the workspace's enabled ones. */
export function usePluginPanels(embedded) {
  const hasEmbedded = Array.isArray(embedded);
  const [state, setState] = useState({ panels: [], loading: true, error: null });

  useEffect(() => {
    if (hasEmbedded) return undefined;
    let cancelled = false;
    loadWorkspacePluginPanels().then(({ panels, error }) => {
      if (!cancelled) setState({ panels, loading: false, error });
    });
    return () => { cancelled = true; };
  }, [hasEmbedded]);

  return hasEmbedded ? { panels: embedded, loading: false, error: null } : state;
}
